import React, { FC, useState, useEffect, ReactNode } from "react"
import { useJSONState, useTranslate } from "../hooks";
import { Switch, TextField, Form, Container, Grid, Button, Centralizer, Select } from "../components";

type InputType = "text" | "number" | "switch" | "select" | "info" | "textArea" | "divider"

type ObjKeyProps<T extends InputType> = {
  name: string;
  type: T;
  required?: T extends "info" ? undefined : boolean;
  readOnly?: T extends "info" ? undefined : boolean;
  max?: number;
  min?: number;
  value?: T extends "switch" ? boolean : 
    T extends "info" ? undefined :
    T extends "number" ? number : string;
  data?: T extends "select" ? {name:string, value:string}[] : never;
};

type ObjType = {
  [key: string]: ObjKeyProps<InputType>;
};

type HeadOptionsType = {
  title?: string,
  okText?: string,
  cancelText?: string,
  hideCancelButton?: boolean,
  validation?: () => Promise<void>
}

type ResultType<T extends Record<string, ObjKeyProps<any>>> = {
  [K in keyof T]: T[K]["type"] extends "switch" ? boolean :
                 T[K]["type"] extends "number" ? number :
                 string;
};

type DialogProps = {
  children?: ReactNode;
  mainDialog?: boolean;
  obj?: ObjType;
};

let dialogCallback: (obj?: ObjType, headOptions?: HeadOptionsType) => void;
let resolvePromise: (result: any | null) => void;

const dialog = {
  open: async <T extends ObjType>(obj: T, headOptions?: HeadOptionsType): Promise<ResultType<T> | null> => {
    return new Promise<ResultType<T> | null>((resolve) => {
      if (dialogCallback) dialogCallback(obj, headOptions);
      resolvePromise = resolve; // Directly assign resolve
    });
  },
  close: () => dialogCallback && dialogCallback(undefined),
  _register: (callback: (obj?: ObjType, headOptions?: HeadOptionsType) => void) => {
    dialogCallback = callback;
  },
};

const Dialog: FC<DialogProps> = ({ mainDialog, children }) => {
  const [dialogState, setDialogState] = useState<{ obj?: ObjType, headOptions?: HeadOptionsType}>({obj: undefined, headOptions:undefined});
  const [ json, setProp, setJson ] = useJSONState<ResultType<ObjType>>({})
  const [ errorMessage, setErrorMessage ] = useState("")
  const { t } = useTranslate()

  useEffect(() => {
    if(mainDialog) dialog._register((obj?: ObjType, headOptions?: HeadOptionsType) => {
      setErrorMessage("")
      setDialogState({ obj, headOptions })
      let json = {} as any
      obj && Object.keys(obj).map(key => {
        let r = obj[key].value ?? (obj[key].type === "switch" ? false : undefined)
        json[key] = r
      })
      setJson(json)
    });
  }, []);

  useEffect(() => {
    const dialogElement = document.querySelector(".schipt-dialog") as HTMLDialogElement;
  
    if (!!children || !!dialogState.obj) {
      dialogElement?.showModal(); // Ensures the dialog behaves as a modal
    } else {
      dialogElement?.close();
    }
  }, [children, dialogState.obj]);


  return (
    <dialog className="schipt-dialog">
      {
        dialogState.obj ? 
        <Container>
        <Form onSubmit={async () => {                
          try{
            if(dialogState.headOptions?.validation) 
              await dialogState.headOptions.validation()

            resolvePromise(json)
            dialog.close()
            return Promise.resolve()
          }catch(e){
            setErrorMessage(e)
            return Promise.resolve()
          }                    
        }}>
          {!dialogState.headOptions?.title ? <></> : <h2 style={{whiteSpace:"pre-line"}}>{dialogState.headOptions.title}</h2>}
          <>
            {Object.keys(dialogState.obj).map((key, i) => {
              if(!dialogState?.obj?.[key]) return <></>
              let o = dialogState.obj[key]

              if (o.type === "info") return <div key={i} className="info">{o.name.split("\n").map(t => <div>{t}</div>)}</div>
              if (o.type === "divider") return <hr key={i}/>

              if(o.type === "switch") return <Switch 
                label={o.name}
                checked={!!json[key]}
                required={o.required} 
                onChange={() => {
                  setProp(!json[key], key)
                  setErrorMessage("")
                }}
                key={i}
              />

              if(o.type === "select")
                return <Select 
                  label={o.name}
                  data={o.data ?? []}
                  value={o.value?.toString()}
                  required={o.required}
                  key={i}
                  style={{marginTop:"1.4rem"}}
                />

              if(o.type === "textArea") 
                return <textarea 
                  className="p-0 min-w-72 min-h-24 border-solid border-2 border-schipt-dark-gray/50 m-2 font-openSans text-sm dark:text-black"
                  value={json[key]}
                  required={o.required} 
                  onChange={(e) => {
                    setProp(e.currentTarget.value, key)
                    setErrorMessage("")
                  }}
                  key={i}
                  placeholder={o.name}
                />

              return <TextField 
                required={o.required} 
                _type={o.type}
                readOnly={o.readOnly}
                max={o.max}
                min={o.min}
                value={json[key]}
                onChange={(e) => {
                  setProp(e.currentTarget.value, key)
                  setErrorMessage("")
                }}
                key={i}
              >{o.name}</TextField>
            })
            }
          </>
          {
            dialogState.headOptions?.hideCancelButton ?
              <Centralizer>
                <Button _type="submit" variant="submit">{dialogState.headOptions?.okText ?? t("ok")}</Button>
              </Centralizer>
            :
            <Grid>
              <Button _type="submit" variant="submit">{dialogState.headOptions?.okText ?? t("ok")}</Button>
              <Button _type="button" variant="undo" onClick={() => {
                  resolvePromise(null)
                  dialog.close()
                }}>{dialogState.headOptions?.cancelText ?? t("close")}</Button>           
            </Grid>            
          }            
            {errorMessage === "" ? <></> :
              <div style={{color: "red"}}>{errorMessage}</div>
            }
        </Form>
        </Container>
        :
        children
      } 
    </dialog>
  );
};


export default Dialog

export { dialog }